/**
 * Refresh Security (unit-11 E-100 to E-112): the hook behind the user-menu item. It lives with the
 * tool because the route and the query keys are the tool's, and `TopBar` stays mounted for the whole
 * session, so a refresh keeps running when the user switches tabs.
 *
 * A refresh never changes a grant: it re-reads the report catalogue and records path moves for
 * review. So there is no confirmation, only progress and an outcome toast.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useIdempotencyKey } from '@/tools/shared/hooks/useIdempotencyKey';
import { readApiError } from './apiErrors';
import { refreshSecurity } from './commands';
import {
  REFRESH_CONFLICT,
  REFRESH_FORBIDDEN,
  REFRESH_PROGRESS_MESSAGE,
  REFRESH_PROGRESS_TITLE,
  REFRESH_SERVER_ERROR,
  REFRESH_SUCCESS_TITLE,
  REFRESH_TIMEOUT,
  REFRESH_UNREACHABLE,
  refreshSuccessMessage,
  type RefreshFailureCopy,
} from './copy';
import { MENU_CONFIG_QUERY_KEY, PENDING_MOVES_QUERY_KEY, TREE_QUERY_KEY } from './queryKeys';
import type { RefreshResult } from './types';

export interface RefreshFailure {
  copy: RefreshFailureCopy;
  /** `forbidden` re-reads the session's permissions; `stale` invalidates the tree and pending moves; `none` does neither. */
  effect: 'forbidden' | 'stale' | 'none';
}

/** E-107: which toast and which side effect each failure gets. */
export function refreshFailureOf(err: unknown): RefreshFailure {
  const { status } = readApiError(err);
  switch (status) {
    case 403:
      return { copy: REFRESH_FORBIDDEN, effect: 'forbidden' };
    case 409:
      return { copy: REFRESH_CONFLICT, effect: 'none' };
    case 502:
      return { copy: REFRESH_UNREACHABLE, effect: 'none' };
    case 504:
    case undefined:
      // API Gateway ends a request at about 29 s, and a request that got no response at all may
      // still have finished on the server.
      return { copy: REFRESH_TIMEOUT, effect: 'stale' };
    default:
      // The catalogue may already be current when a later step fails, so the copy does not say
      // "nothing was changed".
      return { copy: REFRESH_SERVER_ERROR, effect: 'stale' };
  }
}

export interface UseRefreshSecurityResult {
  refresh: () => void;
  isRefreshing: boolean;
}

const successToast = (result: RefreshResult) => ({
  type: 'success' as const,
  title: REFRESH_SUCCESS_TITLE,
  message: refreshSuccessMessage({
    version: result.version,
    reports: result.reports,
    tags: result.tags,
    families: result.families,
    changedCount: result.changedPaths.length,
    pendingMoves: result.pendingMoves,
  }),
  // Persistent while moves wait, so the pointer to the review can be read.
  ...(result.pendingMoves > 0 ? { duration: 0 } : {}),
});

export function useRefreshSecurity(): UseRefreshSecurityResult {
  const queryClient = useQueryClient();
  const { addNotification, removeNotification } = useNotifications();
  const { refreshPermissions } = useAuth();
  // Every selection is a new logical operation (E-110): a replayed failure would defeat a retry.
  const { regenerate } = useIdempotencyKey();
  const progressId = useRef<string | undefined>(undefined);
  // The last outcome toast. A new refresh supersedes it, so persistent outcomes cannot pile up.
  const resultId = useRef<string | undefined>(undefined);
  const mounted = useRef(true);

  const invalidate = useCallback(
    (keys: ReadonlyArray<readonly string[]>) => {
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey: [...queryKey] });
    },
    [queryClient],
  );

  const dropProgress = useCallback(() => {
    if (progressId.current !== undefined) removeNotification(progressId.current);
    progressId.current = undefined;
  }, [removeNotification]);

  const dropResult = useCallback(() => {
    if (resultId.current !== undefined) removeNotification(resultId.current);
    resultId.current = undefined;
  }, [removeNotification]);

  /** Outcome toasts are shown only while the menu's owner is mounted (red-team finding 5). */
  const showResult = useCallback(
    (toast: Parameters<typeof addNotification>[0]) => {
      if (mounted.current) resultId.current = addNotification(toast);
    },
    [addNotification],
  );

  // The top bar unmounts on sign-out. Its persistent toasts describe this session's security, so
  // they go with it; a request still running then settles without adding a toast.
  const cleanupRef = useRef<() => void>(() => undefined);
  cleanupRef.current = () => {
    dropProgress();
    dropResult();
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cleanupRef.current();
    };
  }, []);

  const mutation = useMutation({
    mutationFn: () => refreshSecurity(regenerate()),
    onMutate: () => {
      dropResult();
      progressId.current = addNotification({
        type: 'info',
        title: REFRESH_PROGRESS_TITLE,
        message: REFRESH_PROGRESS_MESSAGE,
        duration: 0,
      });
    },
    onSuccess: (result) => {
      dropProgress();
      showResult(successToast(result));
      // Grants did not change, so the effective-permission caches stay as they are.
      invalidate([TREE_QUERY_KEY, PENDING_MOVES_QUERY_KEY, MENU_CONFIG_QUERY_KEY]);
    },
    onError: (err) => {
      dropProgress();
      const { copy, effect } = refreshFailureOf(err);
      showResult({ ...copy, duration: 0 });
      if (effect === 'stale') invalidate([TREE_QUERY_KEY, PENDING_MOVES_QUERY_KEY]);
      // A failed permission refresh must not turn into a second error.
      if (effect === 'forbidden') refreshPermissions?.().catch((e: unknown) => console.warn('[SecurityControls] permission refresh failed', e));
    },
  });

  const { mutate, isPending } = mutation;
  const refresh = useCallback(() => {
    if (!isPending) mutate();
  }, [isPending, mutate]);

  return { refresh, isRefreshing: isPending };
}
